import { RateSource } from "@redotech/money/rate-source";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useWindowEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupActions } from "@redotech/redo-fulfillment-outbound-labels-common/outbound-label-enums";
import DotIcon from "@redotech/redo-icons/dot-01.svg";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-model/fulfillments/fulfillment-delivery-method-type";
import {
  FulfillmentGroupData,
  shouldUseVerifiedDestinationAddress,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  FulfillmentGroupStatus,
  canPurchaseLabelStatus,
  isDoneStatus,
  isEditingLockedStatus,
} from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import { ShopifyFulfillmentHoldDisplayNames } from "@redotech/redo-model/fulfillments/shopify-fulfillment-schemas";
import {
  OutboundParcel,
  Parcel,
} from "@redotech/redo-model/outbound-labels/parcel";
import { Preset } from "@redotech/redo-model/outbound-labels/presets";
import { TagKind } from "@redotech/redo-model/tag";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { CopyableText } from "@redotech/redo-web/copyable-text";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { LocationDropdown } from "@redotech/redo-web/locations/location-dropdown";
import { Text } from "@redotech/redo-web/text";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EditAddressModalContext,
  EditAddressModalProvider,
} from "../../common/edit-address/edit-address-context";
import { InsuranceSection } from "../../common/insurance-section";
import { MissingItemsModalProvider } from "../../common/missing-items/missing-items-modal-context";
import { OrderFulfillmentAddress } from "../../common/order-fulfillment-address";
import { PackingSlipBinModalProvider } from "../../common/packing-slip-bin-modal-provider";
import { ParcelEditProvider } from "../../common/parcels/parcel-edit-context";
import { PinVerificationProvider } from "../../common/pin-verification-context";
import { PresetSelection } from "../../common/preset-selection";
import {
  ShipmentRateSelectionDropdown,
  SimpleShipmentRateSelectionDropdown,
} from "../../common/shipment-rate-selection-dropdown";
import { FulfillmentGroupStatusBadge } from "../../common/status-badge";
import {
  OutboundRateOption,
  formatTrackingCodesWithLinks,
  getTrackingCodesWithLinks,
} from "../../common/util";
import { useConfirmEditAddress } from "../../hooks/use-confirm-edit-address";
import { usePresetSelection } from "../../hooks/use-preset-selection";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { ParcelContext } from "../../services/parcel-service";
import { isConsolidationShipment } from "../../utils/consolidation-invoice-utils";
import {
  getLocationsForMove,
  getSelectedLocationId,
} from "../../utils/get-groups-locations";
import { getCustomerShippingMethod } from "../../utils/outbound-labels-utils";
import { groupDataPickupInRedoButNotInShopify } from "../../utils/pickup-override";
import { useFulfillmentGroupPermissions } from "../../utils/use-assigned-fulfillment-service";
import type { DisplayParcel } from "../../utils/use-group-info";
import {
  useContainsTiktokShippingOrder,
  useFulfillmentGroupAdjustments,
  useFulfillmentGroupUsedLearning,
  useGroupInfo,
} from "../../utils/use-group-info";
import { useParcelActions } from "../../utils/use-parcel-actions";
import { useRateActions } from "../../utils/use-rate-actions";
import { Tags } from "../cells/tags-cell";
import { ConfirmEditAddressModal } from "../confirm-edit-address-modal";
import { ShipmentAdjustmentsSection } from "../fulfillment-group-slideout/shipment-adjustments-section";
import { ShipmentMessagesCard } from "../fulfillment-group-slideout/shipment-messages-card";
import { ActionButtons } from "./action-buttons";
import { AdditionalSettingsCard } from "./additional-settings-card";
import { CustomDeclarationDetailsCard } from "./customs-details-card";
import { ItemsCard } from "./items-card";
import { MarketplaceNotificationBadge } from "./marketplace-notification";
import { NotesCard } from "./notes-card";
import * as styles from "./outbound-order.module.css";
import { ParcelDetailsCard } from "./parcel-details-card";
import { ParcelPhotosCard } from "./parcel-photos-card";

export const OutboundOrderFulfillmentGroup = memo(
  function OutboundOrderFulfillmentGroup({
    fulfillmentGroup,
    index,
    presets,
    setRefreshSymbol,
    totalGroups,
    handleUpdates,
    updatesPending,
    onGroupsFulfillment,
  }: {
    fulfillmentGroup: FulfillmentGroupData;
    index: number;
    presets: Preset[];
    setRefreshSymbol: (symbol: symbol) => void;
    totalGroups: number;
    handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
    updatesPending: boolean;
    onGroupsFulfillment: (groupIds: string[]) => void;
  }) {
    const [fulfillmentDate, setFulfillmentDate] = useState<Date | null>(null);
    const [notifyMarketplace, setNotifyMarketplace] = useState<boolean>(
      fulfillmentGroup.marketplaceNotification?.status !== "skipped",
    );
    const [parcelBeingEditedIndex, setParcelBeingEditedIndex] = useState(0);
    const selectedStatus = fulfillmentGroup.status;

    useShipmentPageContext(fulfillmentGroup);

    const { permissionsToEditEveryGroup } = useFulfillmentGroupPermissions([
      fulfillmentGroup,
    ]);

    const isEditingLocked = isEditingLockedStatus(fulfillmentGroup.status);

    const displayedButtonAction = canPurchaseLabelStatus(
      fulfillmentGroup.status,
    )
      ? FulfillmentGroupActions.CREATE_AND_PRINT_LABEL
      : FulfillmentGroupActions.PRINT_LABEL;

    const team = useRequiredContext(TeamContext);

    const [parcelsLoad] = useLazyContext(ParcelContext);

    // TODO: Use more fields from this hook
    const {
      selectedRate,
      availableRates,
      trackingNumbers,
      selectedParcelsPerGroup: selectedParcels,
      allowMultipleParcels,
    } = useGroupInfo({
      fulfillmentGroups: [fulfillmentGroup],
      parcelValues: parcelsLoad?.value ?? [],
      availableUsers: [],
      permissionsToEditEveryGroup,
      team,
    });

    const { selectedPreset, setSelectedPreset, onPresetSelect } =
      usePresetSelection([fulfillmentGroup], handleUpdates);

    const { onParcelChange } = useParcelActions({
      fulfillmentGroups: [fulfillmentGroup],
      updateRows: handleUpdates,
      setSelectedPreset,
    });

    const updateParcel = useHandler((parcel: Partial<OutboundParcel>) => {
      onParcelChange(parcel, parcelBeingEditedIndex);
    });

    return (
      <PinVerificationProvider>
        <EditAddressModalProvider>
          <PackingSlipBinModalProvider>
            <MissingItemsModalProvider>
              <ParcelEditProvider
                parcelBeingEditedIndex={parcelBeingEditedIndex}
                parcelsLength={selectedParcels[0]?.length ?? 1}
                setParcelBeingEditedIndex={setParcelBeingEditedIndex}
              >
                <Flex
                  className={styles.shipmentContainer}
                  flexDirection="column"
                  gap="3xl"
                  p="3xl"
                  pb="6xl"
                  w="full"
                >
                  <GroupMainCard
                    allowMultipleParcels={allowMultipleParcels}
                    availableRates={availableRates}
                    displayedButtonAction={displayedButtonAction}
                    everythingDisabled={!permissionsToEditEveryGroup}
                    fulfillmentDate={fulfillmentDate}
                    fulfillmentGroup={fulfillmentGroup}
                    handleUpdates={handleUpdates}
                    index={index}
                    notifyMarketplace={notifyMarketplace}
                    onGroupsFulfillment={onGroupsFulfillment}
                    onPresetSelect={onPresetSelect}
                    parcelBeingEditedIndex={parcelBeingEditedIndex}
                    parcelBeingEditedPerGroup={[
                      selectedParcels[0]![parcelBeingEditedIndex]!,
                    ]}
                    parcelsLoad={parcelsLoad}
                    presets={presets}
                    selectedParcels={selectedParcels[0]!}
                    selectedPreset={selectedPreset}
                    selectedRate={selectedRate}
                    selectedStatus={selectedStatus}
                    setRefreshSymbol={setRefreshSymbol}
                    setSelectedPreset={setSelectedPreset}
                    totalGroups={totalGroups}
                    trackingNumbers={trackingNumbers}
                    updateParcel={updateParcel}
                    updatesPending={updatesPending}
                  />
                  <Flex className={styles.cardColumns}>
                    <Flex
                      flexDirection="column"
                      gap="3xl"
                      height="fit-content"
                      w="half"
                    >
                      <ItemsCard
                        everythingDisabled={!permissionsToEditEveryGroup}
                        fulfillmentGroup={fulfillmentGroup}
                        triggerReload={async () => {
                          setRefreshSymbol(Symbol());
                        }}
                      />
                      <AdditionalSettingsCard
                        disableBulkParcelEditing={false}
                        fulfillmentDate={fulfillmentDate}
                        fulfillmentGroup={fulfillmentGroup}
                        handleUpdates={handleUpdates}
                        notifyMarketplace={notifyMarketplace}
                        parcelBeingEdited={
                          fulfillmentGroup.selectedParcels![
                            parcelBeingEditedIndex
                          ]!
                        }
                        setFulfillmentDate={setFulfillmentDate}
                        setNotifyMarketplace={setNotifyMarketplace}
                        updateParcel={updateParcel}
                      />
                    </Flex>
                    <Flex
                      flexDirection="column"
                      gap="3xl"
                      height="fit-content"
                      pb="3xl"
                      w="half"
                    >
                      <NotesCard
                        fulfillmentGroup={fulfillmentGroup}
                        isEditable={
                          permissionsToEditEveryGroup && !isEditingLocked
                        }
                        updateRows={handleUpdates}
                      />
                      <ParcelPhotosCard fulfillmentGroup={fulfillmentGroup} />
                      <CustomDeclarationDetailsCard
                        fulfillmentGroup={fulfillmentGroup}
                        handleUpdates={handleUpdates}
                        isEditable={
                          permissionsToEditEveryGroup && !isEditingLocked
                        }
                      />
                      <Flex
                        bgColor="primary"
                        borderColor="secondary"
                        borderStyle="solid"
                        borderWidth="1px"
                        dir="column"
                        flexDirection="column"
                        gap="3xl"
                        p="3xl"
                        radius="md"
                        w="full"
                      >
                        <ShipmentMessagesCard
                          fulfillmentGroups={[fulfillmentGroup]}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </ParcelEditProvider>
            </MissingItemsModalProvider>
          </PackingSlipBinModalProvider>
        </EditAddressModalProvider>
      </PinVerificationProvider>
    );
  },
);

const GroupMainCard = memo(function GroupMainCard({
  fulfillmentGroup,
  index,
  presets,
  setRefreshSymbol,
  totalGroups,
  displayedButtonAction,
  onGroupsFulfillment,
  fulfillmentDate,
  notifyMarketplace,
  selectedStatus,
  handleUpdates,
  updatesPending,
  everythingDisabled,
  selectedRate,
  availableRates,
  trackingNumbers,
  parcelsLoad,
  selectedParcels,
  allowMultipleParcels,
  selectedPreset,
  setSelectedPreset,
  onPresetSelect,
  parcelBeingEditedIndex,
  parcelBeingEditedPerGroup,
  updateParcel,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  index: number;
  presets: Preset[];
  setRefreshSymbol: (symbol: symbol) => void;
  totalGroups: number;
  displayedButtonAction: FulfillmentGroupActions;
  fulfillmentDate: Date | null;
  notifyMarketplace: boolean;
  selectedStatus: FulfillmentGroupStatus | undefined | null;
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  updatesPending: boolean;
  everythingDisabled: boolean;
  onGroupsFulfillment: (groupIds: string[]) => void;
  selectedRate: OutboundRateOption | null;
  availableRates: readonly OutboundRateOption[];
  trackingNumbers: string[] | undefined;
  parcelsLoad: LoadState<readonly Parcel[]>;
  selectedParcels: DisplayParcel[];
  allowMultipleParcels: boolean;
  selectedPreset: Preset | undefined;
  setSelectedPreset: (preset: Preset | undefined) => void;
  onPresetSelect: (preset: Preset) => void;
  parcelBeingEditedIndex: number;
  parcelBeingEditedPerGroup: DisplayParcel[];
  updateParcel: (parcel: Partial<OutboundParcel>) => void;
}) {
  return (
    <Flex flexDirection="column" gap="none" w="full">
      <Flex
        bgColor="tertiary"
        borderColor="secondary"
        borderLeftWidth="1px"
        borderStyle="solid"
        borderWidth="1px"
        className={styles.flatBottomCorners}
        p="3xl"
        radius="none"
      >
        <GroupMainCardHeader
          displayedButtonAction={displayedButtonAction}
          everythingDisabled={everythingDisabled}
          fulfillmentDate={fulfillmentDate}
          fulfillmentGroup={fulfillmentGroup}
          handleUpdates={handleUpdates}
          index={index}
          notifyMarketplace={notifyMarketplace}
          onGroupsFulfillment={onGroupsFulfillment}
          onPresetSelect={onPresetSelect}
          parcelValues={parcelsLoad?.value ?? []}
          presets={presets}
          selectedPreset={selectedPreset}
          selectedRate={selectedRate}
          selectedStatus={selectedStatus}
          setRefreshSymbol={setRefreshSymbol}
          setSelectedPreset={setSelectedPreset}
          totalGroups={totalGroups}
          updatesPending={updatesPending}
        />
      </Flex>
      <Flex
        bgColor="primary"
        borderColor="secondary"
        borderLeftWidth="1px"
        borderStyle="solid"
        borderWidth="1px"
        className={styles.flatTopCorners}
        gap="none"
        radius="none"
        w="full"
      >
        <GroupMainCardBody
          allowMultipleParcels={allowMultipleParcels}
          availableRates={availableRates}
          displayedButtonAction={displayedButtonAction}
          everythingDisabled={everythingDisabled}
          fulfillmentGroup={fulfillmentGroup}
          parcelBeingEditedIndex={parcelBeingEditedIndex}
          parcelBeingEditedPerGroup={parcelBeingEditedPerGroup}
          parcelValues={parcelsLoad?.value ?? []}
          selectedParcels={selectedParcels}
          selectedRate={selectedRate}
          selectedStatus={selectedStatus}
          setSelectedPreset={setSelectedPreset}
          trackingNumbers={trackingNumbers}
          triggerRefresh={() => {
            setRefreshSymbol(Symbol());
          }}
          updateParcel={updateParcel}
          updateRows={handleUpdates}
        />
      </Flex>
    </Flex>
  );
});

const GroupMainCardHeader = memo(function GroupMainCardHeader({
  onPresetSelect,
  displayedButtonAction,
  fulfillmentGroup,
  fulfillmentDate,
  index,
  presets,
  setRefreshSymbol,
  totalGroups,
  handleUpdates,
  updatesPending,
  notifyMarketplace,
  selectedRate,
  selectedPreset,
  setSelectedPreset,
  selectedStatus,
  everythingDisabled,
  parcelValues,
  onGroupsFulfillment,
}: {
  onPresetSelect: (preset: Preset) => void;
  displayedButtonAction: FulfillmentGroupActions;
  fulfillmentGroup: FulfillmentGroupData;
  fulfillmentDate: Date | null;
  index: number;
  presets: Preset[];
  setRefreshSymbol: (symbol: symbol) => void;
  notifyMarketplace: boolean;
  selectedRate: OutboundRateOption | null;
  selectedStatus: FulfillmentGroupStatus | undefined | null;
  selectedPreset: Preset | undefined;
  setSelectedPreset: (preset: Preset | undefined) => void;
  totalGroups: number;
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  updatesPending: boolean;
  everythingDisabled: boolean;
  parcelValues: readonly Parcel[];
  onGroupsFulfillment: (groupIds: string[]) => void;
}) {
  const rpcMerchantClient = useRequiredContext(RedoMerchantRpcClientContext);

  const [availableTagsLoad, getAvailableTags] = useTriggerLoad(
    async (signal: AbortSignal) => {
      try {
        const { tags } = await rpcMerchantClient.getTeamTags(
          { kind: TagKind.FULFILLMENT_GROUP },
          { signal },
        );
        return tags;
      } catch (e) {
        return [];
      }
    },
  );

  useEffect(() => {
    getAvailableTags();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useWindowEventListener(window, "keydown", (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (isEditingLockedStatus(selectedStatus)) {
        return;
      }
      for (const preset of presets) {
        if (e.key.toUpperCase() === preset.hotkey.replace("⌘", "")) {
          e.preventDefault();
          onPresetSelect(preset);
        }
      }
    }
  });

  const formattedTrackingCodesWithLinks = useMemo(() => {
    return formatTrackingCodesWithLinks(
      getTrackingCodesWithLinks(fulfillmentGroup),
    );
  }, [fulfillmentGroup]);

  const rateMoney =
    selectedRate?.rate !== undefined
      ? toMoney({ amount: selectedRate.rate, currency: selectedRate.currency })
      : null;

  const providerSource = fulfillmentGroup.providers?.[0];
  const providerDisplayName = providerSource
    ? providerSource.charAt(0).toUpperCase() +
      providerSource.slice(1).toLowerCase()
    : undefined;

  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const openHoldModal = useCallback(() => setHoldModalOpen(true), []);
  const closeHoldModal = useCallback(() => setHoldModalOpen(false), []);

  const holds = fulfillmentGroup.holds;
  const isOnHold =
    fulfillmentGroup.status === FulfillmentGroupStatus.OnHold &&
    holds &&
    holds.length > 0;
  const firstHoldReason = isOnHold
    ? ShopifyFulfillmentHoldDisplayNames[holds[0]!.reason]
    : undefined;

  return (
    <Flex flexDirection="column" gap="3xl" p="xxs" w="full">
      <Flex flexDirection="column" gap="none">
        <Flex className={styles.headerTopRow}>
          <Flex flexDirection="column" gap="xs">
            <Flex align="center" gap="md">
              <Text fontWeight="semibold">
                Shipment {index + 1} of {totalGroups}
              </Text>
              {!canPurchaseLabelStatus(selectedStatus) && (
                <>
                  {isOnHold ? (
                    <div onClick={openHoldModal} style={{ cursor: "pointer" }}>
                      <RedoBadge
                        color="warning"
                        segmentLeading={{ type: "icon", Icon: DotIcon }}
                        size="xs"
                        text={
                          <>
                            On hold:{" "}
                            <span style={{ textDecoration: "underline" }}>
                              {firstHoldReason}
                            </span>
                          </>
                        }
                        type="primary"
                      />
                    </div>
                  ) : (
                    <FulfillmentGroupStatusBadge
                      fulfillmentGroupStatus={fulfillmentGroup.status}
                    />
                  )}
                </>
              )}
            </Flex>
            <Flex w="fit-content">
              <CopyableText
                fontSize="xs"
                preventEventPropagation
                textColor="tertiary"
              >
                {`${fulfillmentGroup._id}`}
              </CopyableText>
            </Flex>
            {fulfillmentGroup.duplicateOf && (
              <Text fontSize="xs" pt="lg" textColor="secondary">
                Reshipped by
                <CopyableText
                  fontSize="xs"
                  preventEventPropagation
                  textColor="tertiary"
                >
                  {`${fulfillmentGroup.duplicateOf}`}
                </CopyableText>
              </Text>
            )}
          </Flex>
          {rateMoney && (
            <Flex
              align="flex-end"
              flexDirection="column"
              gap="xs"
              style={{ flexShrink: 0 }}
            >
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Rate
              </Text>
              <Text fontSize="xs" fontWeight="semibold">
                <MoneyText source={RateSource.Stripe} value={rateMoney} />
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
      {/* Row 2: Tags (full width) | Actions */}
      <Flex align="center" className={styles.headerBottomRow}>
        <Flex
          flexDirection="column"
          gap="xs"
          grow="1"
          shrink="1"
          style={{ minWidth: 0 }}
        >
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Redo shipment tags
          </Text>
          <Tags
            availableTagsLoad={availableTagsLoad}
            data={fulfillmentGroup}
            getAvailableTags={getAvailableTags}
            updateData={handleUpdates}
          />
        </Flex>
        <Flex align="center" style={{ flexShrink: 0 }}>
          <ActionButtons
            buttonSize="sm"
            canPurchaseEveryGroup={canPurchaseLabelStatus(selectedStatus)}
            displayedButtonAction={displayedButtonAction}
            dropdownClassName={styles.headerActionsDropdown}
            fulfillmentDate={fulfillmentDate}
            fulfillmentGroups={[fulfillmentGroup]}
            hideMessages
            hideRate
            mixedPackingSlipSizes={false}
            mixedPersonalizedSlipSizes={false}
            notifyMarketplace={notifyMarketplace}
            onGroupsFulfillment={onGroupsFulfillment}
            parcelValues={parcelValues}
            purchasableGroups={
              canPurchaseLabelStatus(selectedStatus) ? [fulfillmentGroup] : []
            }
            selectedPreset={selectedPreset}
            selectedStatus={selectedStatus}
            setRefreshSymbol={setRefreshSymbol}
            updatesPending={updatesPending}
          />
        </Flex>
      </Flex>
      {/* Row 3: Source + marketplace notification */}
      {(providerDisplayName ||
        (!canPurchaseLabelStatus(selectedStatus) &&
          fulfillmentGroup.marketplaceNotification)) && (
        <Flex align="center" gap="4xl">
          {providerDisplayName && (
            <Flex flexDirection="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Source
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {providerDisplayName}
              </Text>
            </Flex>
          )}
          {!canPurchaseLabelStatus(selectedStatus) &&
            fulfillmentGroup.marketplaceNotification && (
              <Flex flexDirection="column" gap="xs">
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Marketplace notification
                </Text>
                <MarketplaceNotificationBadge group={fulfillmentGroup} />
              </Flex>
            )}
        </Flex>
      )}
      {formattedTrackingCodesWithLinks &&
        formattedTrackingCodesWithLinks.length > 0 && (
          <Flex flexDirection="column" gap="xs">
            <Text fontSize="xs" textColor="secondary">
              Tracking numbers
            </Text>
            <Flex>
              {formattedTrackingCodesWithLinks.map((trackingCodeWithLink) => (
                <CopyableText
                  fontSize="xs"
                  key={trackingCodeWithLink.code}
                  preventEventPropagation
                  textColor="tertiary"
                >
                  {trackingCodeWithLink.code}
                </CopyableText>
              ))}
            </Flex>
          </Flex>
        )}
      {presets.length > 0 && canPurchaseLabelStatus(selectedStatus) && (
        <Flex flexDirection="column">
          <PresetSelection
            disabled={
              everythingDisabled || isEditingLockedStatus(selectedStatus)
            }
            onPresetSelect={onPresetSelect}
            presets={presets}
            selectedPreset={selectedPreset}
          />
        </Flex>
      )}
      {isOnHold && (
        <RedoModal
          isOpen={holdModalOpen}
          onModalCloseRequested={closeHoldModal}
          primaryButton={{ text: "Close", onClick: closeHoldModal }}
          title="Hold Details"
        >
          <Flex flexDirection="column" gap="lg">
            {holds.map((hold, i) => (
              <Flex flexDirection="column" gap="xs" key={hold.holdHandle || i}>
                <Text fontSize="sm" fontWeight="semibold">
                  {ShopifyFulfillmentHoldDisplayNames[hold.reason]}
                </Text>
                {hold.reasonNotes && (
                  <Text fontSize="sm" textColor="secondary">
                    {hold.reasonNotes}
                  </Text>
                )}
              </Flex>
            ))}
          </Flex>
        </RedoModal>
      )}
    </Flex>
  );
});

const GroupMainCardBody = memo(function GroupMainCardBody({
  availableRates,
  displayedButtonAction,
  fulfillmentGroup,
  selectedRate,
  parcelValues,
  selectedStatus,
  setSelectedPreset,
  updateRows,
  triggerRefresh,
  everythingDisabled,
  trackingNumbers,
  selectedParcels,
  allowMultipleParcels,
  parcelBeingEditedIndex,
  parcelBeingEditedPerGroup,
  updateParcel,
}: {
  availableRates: readonly OutboundRateOption[];
  displayedButtonAction: FulfillmentGroupActions;
  fulfillmentGroup: FulfillmentGroupData;
  parcelValues: readonly Parcel[];
  setSelectedPreset: (preset: Preset | undefined) => void;
  selectedRate: OutboundRateOption | null;
  selectedStatus: FulfillmentGroupStatus | undefined | null;
  updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  triggerRefresh: () => void;
  everythingDisabled: boolean;
  trackingNumbers: string[] | undefined;
  selectedParcels: DisplayParcel[];
  allowMultipleParcels: boolean;
  parcelBeingEditedIndex: number;
  parcelBeingEditedPerGroup: DisplayParcel[];
  updateParcel: (parcel: Partial<OutboundParcel>) => void;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const openEditAddressModal = useContext(EditAddressModalContext);

  const {
    confirmEditAddressModalAddress,
    onEditAddress,
    onConfirmEditAddress,
    onUseAutoCleanedAddress,
    closeConfirmModal,
  } = useConfirmEditAddress({
    fulfillmentGroups: [fulfillmentGroup],
    client,
    triggerRefresh,
  });

  const onChangeUseAutoCleanedAddress = onUseAutoCleanedAddress;

  const { onSelectedRateChange } = useRateActions({
    fulfillmentGroups: [fulfillmentGroup],
    updateRows,
    setSelectedPreset,
    openEditAddressModal,
    onEditAddress,
  });

  const { onParcelChange, onRemoveParcel, onParcelAdd, onAllocationsFlush } =
    useParcelActions({
      fulfillmentGroups: [fulfillmentGroup],
      updateRows,
      setSelectedPreset,
    });

  const onSelectedLocationChange = useHandler(
    async (externalLocationId: string) => {
      try {
        const { success } = await client.moveFulfillmentGroups({
          fulfillmentGroupIds: [fulfillmentGroup._id],
          externalLocationId,
        });
        if (success) {
          toast("Changing location...", {
            variant: "success",
            duration: Temporal.Duration.from({ seconds: 2 }),
          });
        } else {
          toast("Error changing location", {
            variant: "error",
            duration: Temporal.Duration.from({ seconds: 2 }),
          });
        }
      } catch (e) {
        toast("Error changing location", {
          variant: "error",
          duration: Temporal.Duration.from({ seconds: 2 }),
        });
      }
    },
  );

  const { usedWeightLearning, usedPackageLearning } =
    useFulfillmentGroupUsedLearning([fulfillmentGroup]);

  const isEditingLocked = isEditingLockedStatus(selectedStatus);

  return (
    <>
      <ConfirmEditAddressModal
        isOpen={confirmEditAddressModalAddress !== null}
        onClose={closeConfirmModal}
        onConfirm={onConfirmEditAddress}
      />
      <Flex
        bgColor="primary"
        className={`${styles.flatTopCorners} ${styles.bodyRow}`}
        gap="none"
        w="full"
      >
        <Flex
          borderColor="secondary"
          borderRightWidth="1px"
          borderStyle="solid"
          p="3xl"
          w="half"
        >
          <DetailsFulfillmentShippingInfoCard
            availableRates={availableRates}
            deliveryMethod={fulfillmentGroup?.deliveryMethod}
            displayedButtonAction={displayedButtonAction}
            everythingDisabled={everythingDisabled}
            fulfillmentGroups={[fulfillmentGroup]}
            mixedRates={false}
            onChangeUseAutoCleanedAddress={onChangeUseAutoCleanedAddress}
            onEditAddress={onEditAddress}
            onSelectedLocationChange={onSelectedLocationChange}
            onSelectedRateChange={onSelectedRateChange}
            parcelBeingEditedIndex={parcelBeingEditedIndex}
            parcelBeingEditedPerGroup={parcelBeingEditedPerGroup}
            requestedShippingService={getCustomerShippingMethod(
              fulfillmentGroup,
            )}
            selectedRate={selectedRate}
            selectedStatus={selectedStatus}
            updateParcel={updateParcel}
            updateRows={updateRows}
          />
        </Flex>
        <Flex p="3xl" w="half">
          <ParcelDetailsCard
            allowMultipleParcels={allowMultipleParcels}
            displayedButtonAction={displayedButtonAction}
            everythingDisabled={everythingDisabled}
            fulfillmentGroupId={fulfillmentGroup._id}
            isEditingLocked={isEditingLocked}
            items={fulfillmentGroup.items}
            mixedParcels={false}
            mixedWeight={false}
            onAllocationsFlush={onAllocationsFlush}
            onParcelAdd={onParcelAdd}
            onParcelChange={onParcelChange}
            onRemoveParcel={onRemoveParcel}
            parcelValues={parcelValues}
            selectedParcels={selectedParcels}
            selectedRate={selectedRate}
            selectedStatus={selectedStatus}
            showParcelPackingSlipButtons={isConsolidationShipment(
              fulfillmentGroup,
            )}
            trackingNumbers={trackingNumbers}
            usedPackageLearning={usedPackageLearning}
            usedWeightLearning={usedWeightLearning}
          />
        </Flex>
      </Flex>
    </>
  );
});

const DetailsFulfillmentShippingInfoCard = memo(
  function DetailsFulfillmentShippingInfoCard({
    availableRates,
    displayedButtonAction,
    deliveryMethod,
    fulfillmentGroups,
    mixedRates,
    onEditAddress,
    onSelectedLocationChange,
    onSelectedRateChange,
    requestedShippingService,
    selectedRate,
    selectedStatus,
    everythingDisabled,
    onChangeUseAutoCleanedAddress,
    parcelBeingEditedIndex,
    parcelBeingEditedPerGroup,
    updateParcel,
    updateRows,
  }: {
    availableRates: readonly OutboundRateOption[];
    displayedButtonAction: FulfillmentGroupActions;
    fulfillmentGroups: readonly FulfillmentGroupData[];
    mixedRates: boolean;
    onEditAddress: (address: FulfillmentOrderAddress) => Promise<void>;
    onSelectedLocationChange: (locationId: string) => void;
    onSelectedRateChange: (
      rate: RedoShopifyDeliveryMethodType | OutboundRateOption,
    ) => void;
    requestedShippingService: string;
    selectedRate: OutboundRateOption | null;
    selectedStatus: FulfillmentGroupStatus | null | undefined;
    deliveryMethod: RedoShopifyDeliveryMethodType | null | undefined;
    everythingDisabled: boolean;
    onChangeUseAutoCleanedAddress: (useAutoCleanedAddress: boolean) => void;
    parcelBeingEditedIndex: number;
    parcelBeingEditedPerGroup: DisplayParcel[];
    updateParcel: (parcel: Partial<OutboundParcel>) => void;
    updateRows: (updates: FulfillmentGroupUpdate[]) => void;
  }) {
    const team = useRequiredContext(TeamContext);

    const { usedShippingLearning } =
      useFulfillmentGroupUsedLearning(fulfillmentGroups);

    const adjustments = useFulfillmentGroupAdjustments(fulfillmentGroups);

    const containsTiktokShippingOrder =
      useContainsTiktokShippingOrder(fulfillmentGroups);

    const totalShippingPaid = useMemo(() => {
      if (
        !fulfillmentGroups.every((group) =>
          Number.isFinite(Number(group.totalShippingCost.amount)),
        )
      ) {
        return undefined;
      }

      // Everything in OMS right now is assuming USD, so I do the same here.
      // See shipping-paid-column.tsx.
      return fulfillmentGroups.reduce((acc, group) => {
        return acc + Number(group.totalShippingCost.amount);
      }, 0);
    }, [fulfillmentGroups]);

    const selectedLocationId = getSelectedLocationId(fulfillmentGroups);
    const locations = getLocationsForMove(fulfillmentGroups);
    const statusPending = fulfillmentGroups.some(
      (group) => group.status === FulfillmentGroupStatus.Pending,
    );
    const ratesPending = fulfillmentGroups.some(
      (group) => group.ratesPendingSince,
    );

    const isPickupInRedoButDeliveryInShopify = useMemo(() => {
      return fulfillmentGroups.some(groupDataPickupInRedoButNotInShopify);
    }, [fulfillmentGroups]);

    const showDumbShipmentRate = useMemo(() => {
      const someClosedWithoutBoughtRate = fulfillmentGroups.some(
        (group) => isDoneStatus(group.status) && !group.shipment?.boughtRate,
      );
      return someClosedWithoutBoughtRate;
    }, [fulfillmentGroups]);

    const displayAddress =
      fulfillmentGroups[0]?.verifiedDestinationAddress &&
      shouldUseVerifiedDestinationAddress(
        fulfillmentGroups[0],
        team.settings?.outboundLabels?.address?.autoCleanAddresses,
      )
        ? fulfillmentGroups[0]?.verifiedDestinationAddress
        : fulfillmentGroups[0]?.destinationAddress;

    return (
      <Flex dir="column" gap="xl" w="full">
        {fulfillmentGroups.length === 1 ? (
          <>
            <Text color="secondary" fontSize="sm" fontWeight="medium">
              Shipping information
            </Text>

            <OrderFulfillmentAddress
              displayAddress={displayAddress}
              hideEditButton={
                everythingDisabled ||
                isEditingLockedStatus(selectedStatus) ||
                containsTiktokShippingOrder
              }
              onChangeUseAutoCleanedAddress={onChangeUseAutoCleanedAddress}
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              originalAddress={fulfillmentGroups[0].destinationAddress}
              updateAddress={onEditAddress}
            />
          </>
        ) : (
          <Text color="secondary" fontSize="sm" fontWeight="medium">
            Bulk update
          </Text>
        )}
        <Flex>
          <LocationDropdown
            disabled={
              everythingDisabled || isEditingLockedStatus(selectedStatus)
            }
            loading={statusPending || ratesPending}
            locations={locations}
            mixed={false}
            onSelectedLocationChange={onSelectedLocationChange}
            selectedLocationId={selectedLocationId}
          />
        </Flex>
        <Text as="span" color="secondary" fontSize="sm" fontWeight="medium">
          <Text fontSize="xs" pb="xs" textColor="secondary">
            Requested shipping service
          </Text>
          <Text fontSize="xs">{requestedShippingService}</Text>
        </Text>

        <Flex flexDirection="column" gap="xs">
          {showDumbShipmentRate ? (
            <SimpleShipmentRateSelectionDropdown groups={fulfillmentGroups} />
          ) : (
            <Flex flexDirection="column" gap="sm">
              <ShipmentRateSelectionDropdown
                deliveryMethod={deliveryMethod}
                disabled={
                  everythingDisabled ||
                  ratesPending ||
                  isEditingLockedStatus(selectedStatus) ||
                  displayedButtonAction ===
                    FulfillmentGroupActions.MARK_AS_FULFILLED ||
                  isPickupInRedoButDeliveryInShopify
                }
                fulfillmentGroups={fulfillmentGroups}
                fullWidth
                includeAlternateDeliveryMethods
                label="Service"
                loading={ratesPending}
                mixedRates={mixedRates}
                options={availableRates}
                selectedRate={selectedRate}
                setSelectedRate={onSelectedRateChange}
                totalShippingPaid={totalShippingPaid}
                usedShippingLearning={usedShippingLearning}
              />
              {fulfillmentGroups[0]?.ratesErrorMessage &&
                fulfillmentGroups.length === 1 && (
                  <Text fontSize="xs" fontWeight="semibold" textColor="error">
                    {fulfillmentGroups[0].ratesErrorMessage}
                  </Text>
                )}
              {adjustments.length > 0 && fulfillmentGroups[0] && (
                <ShipmentAdjustmentsSection
                  adjustments={adjustments}
                  fulfillmentGroupId={fulfillmentGroups[0]._id}
                />
              )}
              <InsuranceSection
                everythingDisabled={everythingDisabled}
                fulfillmentGroups={fulfillmentGroups}
                isEditingLockedStatus={isEditingLockedStatus(selectedStatus)}
                parcelBeingEditedIndex={parcelBeingEditedIndex}
                parcelBeingEditedPerGroup={parcelBeingEditedPerGroup}
                updateParcel={updateParcel}
                updateRows={updateRows}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  },
);

function useShipmentPageContext(fulfillmentGroup: FulfillmentGroupData) {
  const { setPageContext } = useContext(GlobalAgentContext);
  const fulfillmentGroupId = fulfillmentGroup._id;
  const orderName = fulfillmentGroup.orders?.[0]?.name;

  useEffect(() => {
    const displayOrderName = orderName || "Unknown";
    setPageContext?.({
      pageTitle: `Order ${displayOrderName}`,
      pageDescription: `User is viewing shipment for order: ${displayOrderName}, fulfillment group ID: ${fulfillmentGroupId}`,
      preferredAgent: "oms",
      preferredSkill: "oms",
      pageData: { fulfillmentGroupId, orderName: displayOrderName },
    });
    return () => {
      setPageContext?.(undefined);
    };
  }, [setPageContext, fulfillmentGroupId, orderName]);
}
